import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NotificationService, Notification } from '../../services/notification.service';
import { LucideAngularModule, CheckCircle, AlertCircle, AlertTriangle, Info, X } from 'lucide-angular';

@Component({
  selector: 'app-notification',
  standalone: true,
  imports: [CommonModule, LucideAngularModule],
  template: `
    <div class="toast-stack" aria-live="polite" aria-atomic="false">
      <div
        *ngFor="let note of notifications(); trackBy: trackById"
        class="notification-toast"
        [class]="note.type"
        role="alert"
      >
        <div class="notification-content">
          <lucide-icon [name]="getIcon(note.type)" size="18" class="status-icon"></lucide-icon>
          <span class="message">{{ note.message }}</span>
          <button class="close-btn" (click)="dismiss(note.id)" aria-label="Dismiss notification">
            <lucide-icon [name]="XIcon" size="15"></lucide-icon>
          </button>
        </div>
        <div class="progress-bar" [style.animationDuration.ms]="note.duration"></div>
      </div>
    </div>
  `,
  styles: [`
    .toast-stack {
      position: fixed;
      top: 88px;
      right: 24px;
      z-index: 9999;
      display: flex;
      flex-direction: column;
      gap: 10px;
      pointer-events: none;
      max-width: min(460px, calc(100vw - 32px));
    }

    .notification-toast {
      pointer-events: all;
      min-width: 300px;
      background: rgba(18, 18, 18, 0.92);
      backdrop-filter: blur(20px);
      -webkit-backdrop-filter: blur(20px);
      border: 1px solid rgba(255, 255, 255, 0.08);
      border-radius: 12px;
      box-shadow: 0 16px 40px rgba(0, 0, 0, 0.5);
      overflow: hidden;
      animation: toastIn 0.35s cubic-bezier(0.19, 1, 0.22, 1) both;

      &.success { border-left: 3px solid #10b981; }
      &.error   { border-left: 3px solid #ef4444; }
      &.warning { border-left: 3px solid #f59e0b; }
      &.info    { border-left: 3px solid #3b82f6; }
    }

    .notification-content {
      padding: 0.85rem 1rem;
      display: flex;
      align-items: flex-start;
      gap: 0.75rem;
    }

    .status-icon {
      flex-shrink: 0;
      margin-top: 1px;
    }

    .success .status-icon { color: #10b981; }
    .error   .status-icon { color: #ef4444; }
    .warning .status-icon { color: #f59e0b; }
    .info    .status-icon { color: #3b82f6; }

    .message {
      flex: 1;
      color: rgba(255, 255, 255, 0.88);
      font-size: 0.875rem;
      font-family: var(--font-body, sans-serif);
      line-height: 1.5;
    }

    .close-btn {
      flex-shrink: 0;
      background: none;
      border: none;
      color: rgba(255, 255, 255, 0.3);
      cursor: pointer;
      padding: 2px;
      margin-top: 1px;
      transition: color 0.2s;
      line-height: 0;

      &:hover { color: rgba(255, 255, 255, 0.8); }
    }

    .progress-bar {
      height: 2px;
      animation: shrink linear forwards;
    }

    .success .progress-bar { background: #10b981; }
    .error   .progress-bar { background: #ef4444; }
    .warning .progress-bar { background: #f59e0b; }
    .info    .progress-bar { background: #3b82f6; }

    @keyframes toastIn {
      from { transform: translateX(110%); opacity: 0; }
      to   { transform: translateX(0);    opacity: 1; }
    }

    @keyframes shrink {
      from { transform: scaleX(1); transform-origin: left; }
      to   { transform: scaleX(0); transform-origin: left; }
    }

    @media (max-width: 600px) {
      .toast-stack {
        top: auto;
        bottom: 16px;
        right: 12px;
        left: 12px;
        max-width: 100%;
      }
    }
  `]
})
export class NotificationComponent {
  private notificationService = inject(NotificationService);

  readonly notifications = this.notificationService.notifications;
  readonly XIcon = X;

  trackById(_: number, note: Notification): number { return note.id; }

  getIcon(type: string) {
    switch (type) {
      case 'success': return CheckCircle;
      case 'error':   return AlertCircle;
      case 'warning': return AlertTriangle;
      default:        return Info;
    }
  }

  dismiss(id: number): void {
    this.notificationService.dismiss(id);
  }
}
